
import React, { useEffect, useState } from 'react'
import { Tab } from 'react-bootstrap'

import useTranslation from '@/Hooks/UseTranslation'
import { usePlan } from '@/Hooks/UsePlan'

import MainLayout from '@/Layouts/MainLayout/MainLayout'
import ContentHeader from '@/Layouts/MainLayout/components/ContentHeader'

import ExportModal from '@/Components/ExportModal/ExportModal'
import Icon from '@/Components/Icon/Icon'
import MyProjects from './tabs/MyProjects'
import { useDispatch, useSelector } from 'react-redux'
import ProjectsActions from '@/Redux/Actions/ProjectsActions'
import OtherProjects from './tabs/OtherProjects'
import { cleanDashboardAction } from '@/Redux/Actions/DashboardActions'
import { cleanProgrammationAction } from '@/Redux/Actions/ProgrammationActions'
import { cleanMacro } from '@/Redux/Actions/MacroActions'
import { cleanLookahead } from '@/Redux/Actions/LookaheadActions'
import { cleanWeekly } from '@/Redux/Actions/WeeklyActions'
import { cleanTasksAction } from '@/Redux/Actions/TasksActions'
import { cleanResourcesCNCAction } from '@/Redux/Actions/ResourcesCNCActions'
import { cleanIndicatorsPPCAction } from '@/Redux/Actions/IndicatorsPPCActions'
import { cleanResourcesRestrictionsAction } from '@/Redux/Actions/ResourcesRestrictionsActions'
import TeamsActions from '@/Redux/Actions/TeamsActions'
import ProjectCNCActions from '@/Redux/Actions/ProjectCNCActions'
import MainContentBody from '@/Layouts/MainLayout/components/MainContentBody'

const HomePage = () => {

  const { t } = useTranslation()

  const {
    plan,
    tooManyActiveProjects,
    tooManyOthersProjects,
    showTooManyProjectsModal,
    showTooManyOthersProjectsModal,
  } = usePlan()

  const dispatch = useDispatch()

  const [ tab, setTab ] = useState('mine')

  const projects = useSelector((state: any) => state.ProjectsReducer.data)

  const [ showCreateModal, setShowCreateModal ] = useState(false)
  const [ showExportModal, setShowExportModal ] = useState(false)

  const myProjectsCount = projects?.mine?.length || 0
  const sharedProjectsCount = projects?.other?.length || 0
  const archivedProjectsCount = projects?.archived?.length || 0
  const recentProjectName = projects?.mine?.[0]?.shortName || projects?.active?.[0]?.shortName || projects?.other?.[0]?.shortName || 'tu proyecto'
  const summaryProjects = projects?.mine || []
  const progressFallback = [49, 72, 100, 34, 58, 12, 41, 67, 88, 23, 51, 45, 90, 38]
  const getProjectProgress = (_project: any, index: number) => progressFallback[index % progressFallback.length]
  const projectStats = summaryProjects.reduce((acc: any, project: any, index: number) => {
    const progress = getProjectProgress(project, index)

    if (progress >= 100) acc.completed += 1
    else if (progress <= 45) acc.delayed += 1
    else acc.inProgress += 1

    if (progress < 20) acc.planning += 1
    acc.totalProgress += progress

    return acc
  }, { completed: 0, delayed: 0, inProgress: 0, planning: 0, totalProgress: 0 })
  const averageProgress = summaryProjects.length ? Math.round(projectStats.totalProgress / summaryProjects.length) : 0
  const attentionProjects = summaryProjects
    .map((project: any, index: number) => ({
      name: project.shortName || project.name || 'Proyecto',
      progress: getProjectProgress(project, index)
    }))
    .sort((a: any, b: any) => a.progress - b.progress)
    .slice(0, 3)

  const buttons = [
    {
      label: t('HOME:ExportButton'),
      icon: <Icon name="download" fas style={{ marginRight: 4 }} />,
      onClick: () => setShowExportModal(true),
      className: 'pl4-home-export',
      disabled: tab !== 'mine'
    },
    {
      label: t('HOME:NewProjectButton'),
      icon: <Icon name="agregar" />,
      onClick: () => handleCreatePressed(),
      className: 'pl4-home-new',
      disabled: tab !== 'mine'
    }
  ]

  const tabs = [
    {
      label: t('HOME:MyProjectsTab'),
      icon: <i className="far fa-folder" />,
      onClick: () => setTab('mine'),
      active: tab === 'mine',
      badge: myProjectsCount
    },
    {
      label: t('HOME:SharedProyectsTab'),
      icon: <i className="far fa-folder-open" />,
      onClick: () => setTab('others'),
      active: tab === 'others',
      badge: sharedProjectsCount
    },
    {
      label: 'Archivados',
      icon: <i className="fas fa-archive" />,
      onClick: () => undefined,
      active: false,
      disabled: true,
      badge: archivedProjectsCount
    }
  ]

  const handleCreatePressed = () => {
    if (plan.can('create', 'Project')) {

      setShowCreateModal(true);
    } else {

      showTooManyProjectsModal(t('HOME:TooManyProjects-title'))
    }
  }

  useEffect(() => {

    const loadProjects = async () => {

      await dispatch(ProjectsActions.getResultAction())
    }

    loadProjects()
  }, [])

  useEffect(() => {

    const clearData = () => {

      dispatch(ProjectsActions.clearProjectAction())
      dispatch(cleanDashboardAction())
      dispatch(cleanProgrammationAction())
      dispatch(cleanMacro())
      dispatch(cleanLookahead())
      dispatch(cleanWeekly())
      dispatch(cleanTasksAction())
      dispatch(cleanResourcesCNCAction())
      dispatch(cleanIndicatorsPPCAction())
      dispatch(cleanResourcesRestrictionsAction())
      dispatch(TeamsActions.clearDataAction())
      dispatch(ProjectCNCActions.clearAction())

      dispatch({
        type: 'DASHBOARD_LOAD_NONE'
      })
    }

    clearData()
  }, [])

  useEffect(() => {

    if (tooManyActiveProjects)
      showTooManyProjectsModal(t('HOME:TooManyActiveProjects-title'))

    if (tooManyOthersProjects)
      showTooManyOthersProjectsModal(t('HOME:TooManyProjectsYouAreParticipatingIn-title'))
  }, [tooManyActiveProjects, tooManyOthersProjects, projects.active])

  return (
    <>
      <MainLayout menuName="homepage" subMenuName="my_projects" className="pl4-home-layout">
        <div className="pl4-home-page">
          <ContentHeader
            title={t('HOME:Page-title')}
            subtitle={`${myProjectsCount} proyectos · ${recentProjectName} es tu más reciente`}
            buttons={buttons}
            navTabs={tabs}
            className="pl4-home-header"
          />
          <MainContentBody>
            <div className="pl4-home-board">
              <div className="pl4-home-board__main">
                <Tab.Content className='no-padding'>
                  <Tab.Pane active={tab === 'mine'}>
                    <MyProjects
                      showCreateModal={showCreateModal}
                      setShowCreateModal={setShowCreateModal}
                      onCreateProject={handleCreatePressed}
                    />
                  </Tab.Pane>
                  <Tab.Pane active={tab === 'others'}>
                    <OtherProjects />
                  </Tab.Pane>
                </Tab.Content>
              </div>
              <aside className="pl4-home-summary" aria-label="Resumen ejecutivo de proyectos">
              <div className="pl4-home-summary__heading">
                <span>Resumen ejecutivo</span>
                <small>Todos los proyectos</small>
              </div>
              <div className="pl4-home-summary__block">
                <strong>Estado de proyectos</strong>
                <div className="pl4-home-summary__status">
                  <div className="pl4-home-summary__donut" style={{ background: 'conic-gradient(#2f6fed 0 55%, #e5484d 55% 76%, #16a34a 76% 88%, #e8a33d 88% 100%)' }}>
                    <div><strong>{summaryProjects.length}</strong><span>proyectos</span></div>
                  </div>
                  <div className="pl4-home-summary__legend">
                    <span><i style={{ backgroundColor: '#2f6fed' }} /> En curso <strong>{projectStats.inProgress}</strong></span>
                    <span><i style={{ backgroundColor: '#e5484d' }} /> Atrasado <strong>{projectStats.delayed}</strong></span>
                    <span><i style={{ backgroundColor: '#16a34a' }} /> Completado <strong>{projectStats.completed}</strong></span>
                    <span><i style={{ backgroundColor: '#e8a33d' }} /> Planificación <strong>{projectStats.planning}</strong></span>
                  </div>
                </div>
              </div>
              <div className="pl4-home-summary__block pl4-home-summary__average">
                <div className="pl4-home-summary__ring" style={{ '--progress': averageProgress } as React.CSSProperties}>{averageProgress}%</div>
                <div><strong>Avance promedio</strong><span>Global de {summaryProjects.length} proyectos</span></div>
              </div>
              <div className="pl4-home-summary__block pl4-home-summary__attention">
                <strong><i className="fas fa-exclamation-triangle" /> Requieren atención</strong>
                {attentionProjects.map((project: any, index: number) => (
                  <div className="pl4-home-summary__attention-item" key={`${project.name}-${index}`}>
                    <div><span>{project.name}</span><strong>{project.progress}%</strong></div>
                    <div className="pl4-home-summary__attention-track"><span style={{ width: `${project.progress}%` }} /></div>
                  </div>
                ))}
              </div>
              </aside>
            </div>
          </MainContentBody>
        </div>
      </MainLayout>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={(format: string) => console.log(format)}
      />
    </>
  )
}

export default HomePage
